"use client";

import React from "react";
import { Link } from "@/components/common/Link";
import { Card, CardContent, CardFooter, CardHeader, CardTitle } from "@/components/ui/card";
import { EcommerceProductsProvider, useEcommerceProducts, AddToCartButton } from "@/integrations/wordpress/WordPressEcommerceProductsProvider";
import { WP_Query } from "@/integrations/wordpress/wp_query";
const staticProducts = [{
  title: "Panel nameplates",
  desc: "Equipment ID and rating nameplates for the panel enclosure.",
  link: "/products/engraved-nameplates/panel-nameplates/",
  img: "https://wpvc-images.s3.us-east-1.amazonaws.com/images/1703715/img/panel_nameplates.png",
  srcset: "https://wpvc-images.s3.us-east-1.amazonaws.com/images/1703715/img/panel_nameplates.png-wvc-srcset",
  sizes: "https://wpvc-images.s3.us-east-1.amazonaws.com/images/1703715/img/panel_nameplates.png-wvc-sizes"
}, {
  title: "Equipment nameplates",
  desc: "Component and device nameplates with model, serial, and rating fields.",
  link: "/products/engraved-nameplates/equipment-nameplates/",
  img: "https://wpvc-images.s3.us-east-1.amazonaws.com/images/1703715/img/equipment_nameplates.png",
  srcset: "https://wpvc-images.s3.us-east-1.amazonaws.com/images/1703715/img/equipment_nameplates.png-wvc-srcset",
  sizes: "https://wpvc-images.s3.us-east-1.amazonaws.com/images/1703715/img/equipment_nameplates.png-wvc-sizes"
}, {
  title: "Legend plates",
  desc: "Engraved legends for pushbuttons, selectors, and indicators.",
  link: "/products/phenolic-legend-plates/",
  img: "https://wpvc-images.s3.us-east-1.amazonaws.com/images/1703715/img/legend_plates.png",
  srcset: "https://wpvc-images.s3.us-east-1.amazonaws.com/images/1703715/img/legend_plates.png-wvc-srcset",
  sizes: "https://wpvc-images.s3.us-east-1.amazonaws.com/images/1703715/img/legend_plates.png-wvc-sizes"
}, {
  title: "Pushbutton legend plates",
  desc: "22mm and 30mm operator-interface plates.",
  link: "/products/pushbutton-legend-plates/",
  img: "https://wpvc-images.s3.us-east-1.amazonaws.com/images/1703715/img/pushbutton_legend_plates.png",
  srcset: "https://wpvc-images.s3.us-east-1.amazonaws.com/images/1703715/img/pushbutton_legend_plates.png-wvc-srcset",
  sizes: "https://wpvc-images.s3.us-east-1.amazonaws.com/images/1703715/img/pushbutton_legend_plates.png-wvc-sizes"
}, {
  title: "Electrical panel schedules",
  desc: "Engraved directory/schedule plates identifying each circuit or device.",
  link: "/products/electrical-panel-schedules/",
  img: "https://wpvc-images.s3.us-east-1.amazonaws.com/images/1703715/img/electrical_panel_schedules.png",
  srcset: "https://wpvc-images.s3.us-east-1.amazonaws.com/images/1703715/img/electrical_panel_schedules.png-wvc-srcset",
  sizes: "https://wpvc-images.s3.us-east-1.amazonaws.com/images/1703715/img/electrical_panel_schedules.png-wvc-sizes"
}, {
  title: "Arc flash labels",
  desc: "Produced from your arc flash study data, in durable construction.",
  link: "/products/arc-flash-labels/",
  img: "https://wpvc-images.s3.us-east-1.amazonaws.com/images/1703715/img/arc_flash_labels.png",
  srcset: "https://wpvc-images.s3.us-east-1.amazonaws.com/images/1703715/img/arc_flash_labels.png-wvc-srcset",
  sizes: "https://wpvc-images.s3.us-east-1.amazonaws.com/images/1703715/img/arc_flash_labels.png-wvc-sizes"
}];
function ProductsGridConsumer() {
  const {
    products
  } = useEcommerceProducts();
  const displayItems = products.length > 0 ? products.slice(0, 6).map(p => ({
    id: p.id,
    title: p.title,
    desc: p.short_description || "",
    link: p.link,
    img: p.featured_image_url,
    srcset: "",
    sizes: ""
  })) : staticProducts;
  return <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-6 mt-12">
      {displayItems.map((item, i) => <Card key={item.id || i} data-index={i} className="bg-secondary border-white/10 overflow-hidden flex flex-col shadow-lg rounded-xl">
          <Link to={item.link} className="block aspect-[4/3] bg-[var(--color-logo)] relative overflow-hidden group">
            {item.img ? <img src={item.img} {...item.srcset ? {
          'data-wvc-srcset': item.srcset,
          'data-wvc-sizes': item.sizes
        } : {}} alt={item.title} className="object-cover w-full h-full transition-transform duration-500 group-hover:scale-105" loading="lazy" /> : <div className="w-full h-full flex items-center justify-center bg-[var(--color-logo)]">
                <span className="text-primary font-semibold font-default">{item.title}</span>
              </div>}
          </Link>
          <CardHeader className="pb-3">
            <CardTitle className="text-white text-xl font-default">
              <h3 className="m-0">
                <Link to={item.link} className="hover:text-primary transition-colors">{item.title}</Link>
              </h3>
            </CardTitle>
          </CardHeader>
          <CardContent className="flex-grow">
            <p className="text-[#E8E4DC] text-sm leading-relaxed font-default" dangerouslySetInnerHTML={{
          __html: item.desc
        }} />
          </CardContent>
          {item.id && <CardFooter className="pt-0">
              <AddToCartButton productId={item.id} variant="default" className="w-full" buttonClassName="bg-primary text-primary-foreground w-full hover:bg-primary/90" text="Add to Cart" showQuantity={false} />
            </CardFooter>}
        </Card>)}
    </div>;
}
export default function WhatAUL508APanelNeeds() {
  const wp_query_params = {
    per_page: 6,
    post_type: "product"
  };
  const wp_query = new WP_Query(wp_query_params);
  return <section id="what-a-ul-508a-panel-needs" className="bg-[var(--color-logo)] py-20 px-6 md:px-12 lg:px-24">
      <div className="max-w-7xl mx-auto">
        <div className="max-w-3xl">
          <h2 className="text-3xl md:text-4xl font-bold text-white font-default mb-6">
            What Marking Does a UL 508A Panel Need?
          </h2>
          <p className="text-lg text-[#E8E4DC] font-default">
            The marking Evermark Industrial Solutions produces for UL 508A panels, mapped to where it goes:
          </p>
        </div>

        <EcommerceProductsProvider wp_query={{
        per_page: 6,
        post_type: "product"
      }}>
          <ProductsGridConsumer />
        </EcommerceProductsProvider>

        <div className="mt-12 p-6 bg-secondary/50 border border-white/10 rounded-xl">
          <p className="text-sm text-[#E8E4DC] leading-relaxed font-default">
            <span className="font-semibold text-white">Note:</span> Exactly which markings a given panel requires depends on the panel design, its ratings, and the UL 508A provisions that apply — Evermark recommends confirming the marking schedule with the shop's UL program or a qualified specialist.
          </p>
        </div>
      </div>
    </section>;
}